import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  CreditCard,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Clock,
  XCircle,
  TrendingUp,
  Calendar,
  HardDrive,
  Mic,
  FileText,
  ExternalLink,
  Users,
  Crown,
  Shield,
  Zap,
  Check,
  X,
  ArrowRight,
  Sparkles,
  Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  trialing: {
    label: "Trial",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    icon: Clock,
    gradient: "from-blue-500 to-blue-600",
  },
  active: {
    label: "Active",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
    icon: CheckCircle,
    gradient: "from-emerald-500 to-emerald-600",
  },
  past_due: {
    label: "Past Due",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    icon: AlertTriangle,
    gradient: "from-amber-500 to-amber-600",
  },
  canceled: {
    label: "Canceled",
    color: "bg-red-100 text-red-700 border-red-200",
    icon: XCircle,
    gradient: "from-red-500 to-red-600",
  },
};

export default function BillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState(null);

  useEffect(() => {
    fetchBillingData();
    
    // Check for success redirect
    if (searchParams.get("session_id")) {
      toast.success("Subscription updated successfully!");
    }
  }, [searchParams]);

  const fetchBillingData = async () => {
    try {
      const [subRes, usageRes] = await Promise.all([
        axios.get(`${API}/stripe-billing/subscription-status`),
        axios.get(`${API}/gates/usage-summary`)
      ]);
      setSubscription(subRes.data);
      setUsage(usageRes.data.usage);
    } catch (error) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan, interval = "month") => {
    setUpgrading(true);
    try {
      const response = await axios.post(`${API}/stripe-billing/create-checkout-session`, {
        plan,
        interval,
        promo_code: promoCode || undefined
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(`${API}/stripe-billing/create-portal-session`);
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      toast.error("Failed to open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    try {
      const response = await axios.post(`${API}/stripe-billing/validate-promo-code`, {
        code: promoCode
      });
      setPromoValidation(response.data);
      if (response.data.valid) {
        toast.success(`Code applied: ${response.data.discount_percent}% off!`);
      } else {
        toast.error(response.data.message || "Invalid code");
      }
    } catch (error) {
      toast.error("Failed to validate code");
    }
  };

  const getUsageColor = (percent) => {
    if (percent >= 100) return "from-red-500 to-red-600";
    if (percent >= 80) return "from-amber-500 to-orange-500";
    return "from-teal-500 to-teal-600";
  };

  const getUsageTextColor = (percent) => {
    if (percent >= 100) return "text-red-600";
    if (percent >= 80) return "text-amber-600";
    return "text-slate-800";
  };

  const statusConfig = subscription ? STATUS_CONFIG[subscription.status] || STATUS_CONFIG.active : null;
  const StatusIcon = statusConfig?.icon || CheckCircle;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Billing & Usage</h1>
                <p className="text-sm text-slate-500">Manage your subscription and view usage</p>
              </div>
            </div>
            {subscription?.stripe_customer_id && (
              <Button
                variant="outline"
                onClick={handleManageBilling}
                disabled={portalLoading}
              >
                {portalLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Manage Billing
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Current Plan Card */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden mb-8">
          <div className={`bg-gradient-to-r ${statusConfig?.gradient || "from-teal-500 to-teal-600"} p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  {subscription?.plan_tier === "premium" || subscription?.plan_tier === "premium_governance" ? (
                    <Crown className="w-7 h-7" />
                  ) : subscription?.plan_tier === "enterprise" ? (
                    <Building2 className="w-7 h-7" />
                  ) : (
                    <Zap className="w-7 h-7" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{subscription?.plan_name || "Standard"}</h2>
                  <p className="text-white/80">Current Plan</p>
                </div>
              </div>
              <div className="text-right">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${statusConfig?.color} text-sm font-semibold`}>
                  <StatusIcon className="w-4 h-4" />
                  {statusConfig?.label || "Active"}
                </div>
                {subscription?.current_period_end && (
                  <p className="text-white/70 text-sm mt-2">
                    Renews: {new Date(subscription.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Usage Section */}
          <div className="p-6" id="usage">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">This Month&apos;s Usage</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {usage?.map((item) => (
                <div key={item.type} className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {item.type === "meetings" && <FileText className="w-4 h-4 text-slate-500" />}
                      {item.type === "audio_hours" && <Mic className="w-4 h-4 text-slate-500" />}
                      {item.type === "storage" && <HardDrive className="w-4 h-4 text-slate-500" />}
                      {item.type === "users" && <Users className="w-4 h-4 text-slate-500" />}
                      <span className="text-sm font-medium text-slate-600 capitalize">
                        {item.display_name}
                      </span>
                    </div>
                    <span className={`text-sm font-bold ${getUsageTextColor(item.percent)}`}>
                      {item.used} / {item.limit}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${getUsageColor(item.percent)} rounded-full transition-all duration-500`}
                      style={{ width: `${item.percent}%` }}
                    />
                  </div>
                  {item.near_limit && !item.at_limit && (
                    <p className="text-xs text-amber-600 mt-2">
                      Premium includes {item.premium_limit} {item.display_name}
                    </p>
                  )}
                  {item.at_limit && (
                    <p className="text-xs text-red-600 mt-2 font-medium">
                      Limit reached! Upgrade to continue
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upgrade Section */}
        {subscription?.plan_tier !== "premium" && subscription?.plan_tier !== "premium_governance" && subscription?.plan_tier !== "enterprise" && (
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden mb-8">
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">Upgrade Your Plan</h3>
              <p className="text-sm text-slate-500 mt-1">
                Unlock governance-grade features for public company compliance
              </p>
            </div>
            
            <div className="p-6">
              {/* Promo Code Input */}
              <div className="flex gap-3 mb-6 max-w-md">
                <Input
                  placeholder="Promo code (e.g., NEWYEAR20)"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="flex-1"
                />
                <Button variant="outline" onClick={validatePromoCode}>
                  Apply
                </Button>
              </div>
              
              {promoValidation?.valid && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 max-w-md">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">{promoValidation.discount_percent}% off applied!</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    {promoValidation.name} - {promoValidation.duration === "repeating" 
                      ? `for ${promoValidation.duration_months} months` 
                      : promoValidation.duration}
                  </p>
                </div>
              )}
              
              {/* Plan Comparison */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Standard */}
                <div className={`border-2 rounded-2xl p-6 ${subscription?.plan_tier === "standard" ? "border-teal-500 bg-teal-50/30" : "border-slate-200"}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Standard</h4>
                      <p className="text-sm text-slate-500">For small teams</p>
                    </div>
                    {subscription?.plan_tier === "standard" && (
                      <span className="ml-auto px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-slate-800 mb-4">
                    $199<span className="text-base font-normal text-slate-500">/mo</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {["10 meetings/month", "20 audio hours", "50GB storage", "10 users", "Basic exports"].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-teal-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {subscription?.plan_tier !== "standard" && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => handleUpgrade("standard")}
                      disabled={upgrading}
                    >
                      Downgrade
                    </Button>
                  )}
                </div>
                
                {/* Premium Governance */}
                <div className="border-2 border-purple-300 rounded-2xl p-6 bg-purple-50/30 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-700 text-white text-xs font-semibold rounded-full">
                      Recommended
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Premium Governance</h4>
                      <p className="text-sm text-slate-500">For public companies</p>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800 mb-4">
                    $599<span className="text-base font-normal text-slate-500">/mo</span>
                    {promoValidation?.valid && (
                      <span className="ml-2 text-base font-normal text-green-600 line-through">
                        ${Math.round(599 * (1 - promoValidation.discount_percent / 100))}
                      </span>
                    )}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {[
                      "40 meetings/month",
                      "80 audio hours",
                      "200GB storage",
                      "25 users",
                      "Chair signing & locking",
                      "Governance linting",
                      "Action register",
                      "Board pack export",
                      "Audit log & access logs"
                    ].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-purple-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
                    onClick={() => handleUpgrade("premium")}
                    disabled={upgrading}
                  >
                    {upgrading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Upgrade to Premium
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enterprise CTA */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-center">
          <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Need SSO, RBAC, or an SLA?</h3>
          <p className="text-slate-300 mb-6 max-w-md mx-auto">
            Enterprise includes Microsoft Entra ID & Google SSO, role-based access control,
            dedicated support, and custom SLA guarantees.
          </p>
          <Button
            variant="outline"
            className="border-slate-600 text-white hover:bg-slate-700"
            onClick={() => window.location.href = "mailto:sales@mymeetingspal.com?subject=Enterprise%20Plan%20Inquiry"}
          >
            Contact Sales
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
